import React, { useState, useEffect, useRef } from 'react';
import { Datepicker } from '@meinefinsternis/react-horizontal-date-picker';
import { enUS } from 'date-fns/locale';
import { FaArrowLeft } from 'react-icons/fa';
import {jwtDecode} from 'jwt-decode'; // Import jwt-decode
import TurfInfo from '../Booking/TurfInfo';
import SlotCard from '../Booking/SlotCard';
import '../styles.css';

const BookingPopup = ({
  showPopup,
  handleClosePopup,
  selectedDate, 
  setSelectedDate,
  numPeople,
  setNumPeople,
  selectedSlot,
  setSelectedSlot,
  handleSlotSelection,
  totalAmount,
  calculateTotalAmount,
  handleBooking,
  productId,
  venueDetails
}) => {
  const [date, setDate] = useState(new Date());
  const [showSlotSelection, setShowSlotSelection] = useState(false);
  const [slots, setSlots] = useState([]);
  const popupRef = useRef(null);
  //console.log('venue',venueDetails)
  useEffect(() => {
    setSelectedDate(date);
  }, [date, setSelectedDate]);

  useEffect(() => {
    const fetchSlots = async () => {
      if (showPopup && productId) {
        try {
          const response = await fetch(`http://localhost:7254/api/bookings/getByTimeSlot/?productid=${productId}`);
          if (response.ok) {
            const data = await response.json();
            const slotsWithPrice = data.responseContents.map(slot => ({
              ...slot,
              price: 500 // Assign price as 500 INR
            }));
            setSlots(slotsWithPrice);
          } else {
            console.error('Failed to fetch slots:', response.status, response.statusText);
          }
        } catch (error) {
          console.error('Error fetching slots:', error);
        }
      }
    };

    fetchSlots();
  }, [showPopup, productId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        handleClosePopup();
      }
    };

    if (showPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPopup, handleClosePopup]);

  const handleDateChange = (d) => {
    const [selectedDate] = d;
    setDate(selectedDate);
    setSelectedDate(selectedDate);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', { dateStyle: 'full' }).format(date);
  };

  const handleConfirmBooking = async () => {
    const token = localStorage.getItem('google_token');
    let userId = '';

    if (token) {
      try {
        const decodedToken = jwtDecode(token); // Decode the token
        console.log('Decoded Token:', decodedToken);

        // Parse the 'sub' field to get the email ID
        let sub = decodedToken.sub;
        if (typeof sub === 'string') {
          try {
            sub = JSON.parse(sub); // Try parsing if it's a stringified JSON
          } catch (parseError) {
            console.warn('Error parsing sub field:', parseError);
          }
        }

        // Access email ID safely
        userId = sub?.email_id || ''; // Use optional chaining to handle undefined
      } catch (error) {
        console.error('Error decoding token:', error);
        return; // Exit if the token cannot be decoded
      }
    }

    if (!selectedSlot) return;

    // Prepare the booking details
    const bookingData = {
      bookingDate: selectedDate.toISOString(),
      bookingId: '', // Generate or leave empty
      clientId: selectedSlot.clientId,
      eventEndTime: `${selectedDate.toISOString().split('T')[0]}T${selectedSlot.endTime}`,
      eventStartTime: `${selectedDate.toISOString().split('T')[0]}T${selectedSlot.startTime}`,
      productId: productId,
      slotids: [selectedSlot.id],
      status: 'pending',
      totalPrice: totalAmount,
      turfId: productId, // Assuming turfId is the same as productId
      userId: userId
    };

    console.log('Request Data:', bookingData);

    try {
      const response = await fetch('http://localhost:7254/api/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Booking Successful:', data);
        handleClosePopup();
      } else {
        console.error('Booking Failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error during booking:', error);
    }
  };

  if (!showPopup) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div
        ref={popupRef}
        className="bg-white rounded-lg overflow-hidden w-11/12 md:w-3/4 lg:w-2/3 relative flex flex-col md:flex-row"
        style={{ height: '600px', maxWidth: '900px' }}
      >
        {/* Close Button */}
        <button
          className="absolute top-4 right-4 text-red-500 text-2xl font-bold"
          onClick={handleClosePopup}
        >
          &times;
        </button>

        {/* Left Section: Turf Information */}
        <TurfInfo venueDetails={venueDetails} productId={productId} />

        {/* Right Section: Booking Form */}
        <div className="w-full md:w-1/2 p-6 flex flex-col justify-between h-full">
          <div className="flex items-center justify-between mb-4">
            {showSlotSelection && (
              <FaArrowLeft
                className="text-gray-500 cursor-pointer hover:text-gray-700"
                onClick={() => setShowSlotSelection(false)}
              />
            )}
            <h1 className="text-3xl font-bold text-center flex-1">
              Book Your Slot
            </h1>
          </div>

          <div className="flex-1 flex flex-col justify-between">
            {!showSlotSelection ? (
              <>
                <div className="mb-4">
                  <label className="block font-medium mb-2">Select Date</label>
                  <Datepicker
                    onChange={handleDateChange}
                    locale={enUS}
                    startValue={date}
                    endValue={date}
                    className={{
                      selectedDay: 'selected-date',
                      day: 'day',
                    }}
                    startDate={new Date()}
                    endDate={new Date(new Date().setMonth(new Date().getMonth() + 3))}
                  />
                </div>

                <div className="mb-4">
                  <label className="block font-medium mb-2">Number of People</label>
                  <input
                    type="number"
                    min="1"
                    value={numPeople}
                    onChange={(e) => setNumPeople(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>

                <button
                  className="w-full bg-blue-500 text-white p-3 rounded-md hover:bg-blue-600 mt-4"
                  onClick={() => setShowSlotSelection(true)}
                  disabled={!selectedDate || !numPeople}
                >
                  Continue
                </button>
              </>
            ) : (
              <>
                <div className="mb-4 text-center">
                  <h2 className="text-lg font-medium">Selected Date</h2>
                  <p className="text-blue-500 font-semibold">{formatDate(selectedDate)}</p>
                </div>

                <div className="mb-4">
                  <h2 className="text-lg font-medium mb-2">Select Time Slot</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {slots.map((slot, index) => (
                      <SlotCard
                        key={index}
                        slot={slot}
                        selectedSlot={selectedSlot}
                        handleSlotSelection={handleSlotSelection}
                      />
                    ))}
                  </div>
                </div>

                <h3 className="text-lg font-semibold">Total Amount: ₹{totalAmount}</h3>
                <button
                  className="w-full bg-blue-500 text-white p-3 rounded-md hover:bg-blue-600 mt-4"
                  onClick={handleConfirmBooking}
                  disabled={!selectedSlot}
                >
                  Book Now
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPopup;
